import { useState, useMemo } from "react";
import { Ticket, Copy, XCircle, AlertCircle, Plus, Search, Clock } from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import { KPICard, AdminModal, EmptyState } from "@/components/admin/shared";
import {
  useCoupons,
  useCreateCoupon,
  useBulkCreateCoupons,
  useRevokeCoupon,
  usePromotions,
} from "@/hooks/admin/useMarketing";
import type {
  CouponStatus,
  CreateCouponInput,
  BulkCreateCouponsInput,
  CouponListParams,
} from "@/lib/admin-api/marketing";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_LABELS: Record<CouponStatus, string> = {
  active: "Actif",
  used: "Utilisé",
  expired: "Expiré",
  revoked: "Révoqué",
};

const STATUS_STYLES: Record<CouponStatus, string> = {
  active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  used: "bg-stone-100 text-stone-600 border-stone-200",
  expired: "bg-red-100 text-red-700 border-red-200",
  revoked: "bg-red-100 text-red-700 border-red-200",
};

// ---------------------------------------------------------------------------
// Usage indicator
// ---------------------------------------------------------------------------

function UsageIndicator({
  usesCount,
  maxUses,
}: {
  readonly usesCount: number;
  readonly maxUses: number | null;
}) {
  if (maxUses == null) {
    return (
      <div className="flex items-center gap-1.5">
        <span className="tabular-nums text-stone-600">{usesCount}</span>
        <span className="text-xs text-stone-400">/ illimite</span>
      </div>
    );
  }

  const pct = Math.min((usesCount / maxUses) * 100, 100);
  const isExhausted = usesCount >= maxUses;

  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-14 rounded-full bg-stone-100">
        <div
          className={`h-full rounded-full transition-all ${
            isExhausted
              ? "bg-red-500"
              : pct >= 80
              ? "bg-amber-500"
              : "bg-emerald-500"
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="tabular-nums text-xs text-stone-600">
        {usesCount} / {maxUses}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Single coupon modal
// ---------------------------------------------------------------------------

function CreateCouponModal({
  open,
  onClose,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
}) {
  const { data: promoData } = usePromotions({ limit: 100 });
  const create = useCreateCoupon();
  const [code, setCode] = useState("");
  const [promoId, setPromoId] = useState("");
  const [maxUses, setMaxUses] = useState("1");
  const [expiresAt, setExpiresAt] = useState("");

  const handleSubmit = () => {
    if (!code.trim()) {
      toast.error("Le code est obligatoire.");
      return;
    }
    if (!promoId) {
      toast.error("Selectionnez une promotion.");
      return;
    }
    const mu = maxUses ? parseInt(maxUses, 10) : null;
    if (mu !== null && (isNaN(mu) || mu < 1)) {
      toast.error("Nombre d'utilisations invalide.");
      return;
    }

    const input: CreateCouponInput = {
      code: code.trim().toUpperCase(),
      promotion_id: promoId,
      max_uses: mu,
    };
    create.mutate(input, {
      onSuccess: () => {
        toast.success("Coupon cree.");
        onClose();
      },
      onError: () => toast.error("Erreur lors de la creation du coupon."),
    });
  };

  return (
    <AdminModal
      open={open}
      onClose={onClose}
      title="Creer un coupon"
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={create.isPending}>
            {create.isPending ? "En cours..." : "Creer"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-1">
          <label className="text-sm font-medium text-stone-700">
            Code <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="ex : PROMO20"
            className="w-full rounded-lg border border-stone-200 px-3 py-2 font-mono text-sm uppercase focus:outline-none focus:ring-2 focus:ring-amber-300"
          />
          <p className="text-xs text-stone-400">
            Le code sera automatiquement converti en majuscules.
          </p>
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium text-stone-700">
            Promotion liee <span className="text-red-500">*</span>
          </label>
          <select
            value={promoId}
            onChange={(e) => setPromoId(e.target.value)}
            className="w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
          >
            <option value="">Selectionner une promotion</option>
            {promoData?.promotions.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="text-sm font-medium text-stone-700">
              Max utilisations
            </label>
            <input
              type="number"
              value={maxUses}
              onChange={(e) => setMaxUses(e.target.value)}
              placeholder="1"
              min={1}
              className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
            />
            <p className="text-xs text-stone-400">
              1 = usage unique, vide = illimite
            </p>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-stone-700">
              Date d'expiration
            </label>
            <input
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
            />
          </div>
        </div>
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Bulk create modal
// ---------------------------------------------------------------------------

function BulkCreateModal({
  open,
  onClose,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
}) {
  const { data: promoData } = usePromotions({ limit: 100 });
  const bulkCreate = useBulkCreateCoupons();
  const [promoId, setPromoId] = useState("");
  const [prefix, setPrefix] = useState("");
  const [count, setCount] = useState("10");
  const [maxUsesEach, setMaxUsesEach] = useState("1");

  const handleSubmit = () => {
    if (!promoId) {
      toast.error("Selectionnez une promotion.");
      return;
    }
    if (!prefix.trim()) {
      toast.error("Le prefixe est obligatoire.");
      return;
    }
    const n = parseInt(count, 10);
    if (isNaN(n) || n < 1) {
      toast.error("Nombre de coupons invalide.");
      return;
    }
    if (n > 500) {
      toast.error("Maximum 500 coupons par generation.");
      return;
    }

    const input: BulkCreateCouponsInput = {
      promotion_id: promoId,
      prefix: prefix.trim().toUpperCase(),
      count: n,
      max_uses_each: maxUsesEach ? parseInt(maxUsesEach, 10) : null,
    };
    bulkCreate.mutate(input, {
      onSuccess: (res) => {
        toast.success(`${res.created} coupons crees.`);
        onClose();
      },
      onError: () => toast.error("Erreur lors de la creation en masse."),
    });
  };

  // Preview
  const previewCode = prefix
    ? `${prefix.toUpperCase()}-XXXX`
    : "PREFIXE-XXXX";

  return (
    <AdminModal
      open={open}
      onClose={onClose}
      title="Creer des coupons en masse"
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={bulkCreate.isPending}>
            {bulkCreate.isPending ? "Génération..." : "Générer"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-1">
          <label className="text-sm font-medium text-stone-700">
            Promotion liee <span className="text-red-500">*</span>
          </label>
          <select
            value={promoId}
            onChange={(e) => setPromoId(e.target.value)}
            className="w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
          >
            <option value="">Selectionner une promotion</option>
            {promoData?.promotions.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="text-sm font-medium text-stone-700">
              Prefixe <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={prefix}
              onChange={(e) => setPrefix(e.target.value.toUpperCase())}
              placeholder="ex : NOEL"
              className="w-full rounded-lg border border-stone-200 px-3 py-2 font-mono text-sm uppercase focus:outline-none focus:ring-2 focus:ring-amber-300"
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-stone-700">
              Nombre de coupons <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={count}
              onChange={(e) => setCount(e.target.value)}
              min={1}
              max={500}
              className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
            />
          </div>
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium text-stone-700">
            Max utilisations par coupon
          </label>
          <input
            type="number"
            value={maxUsesEach}
            onChange={(e) => setMaxUsesEach(e.target.value)}
            min={1}
            placeholder="1"
            className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
          />
        </div>
        <div className="rounded-lg bg-stone-50 px-4 py-3 space-y-1">
          <p className="text-xs font-medium text-stone-500">
            Apercu du format
          </p>
          <p className="font-mono text-sm text-stone-700">{previewCode}</p>
          <p className="text-xs text-stone-400">
            {count || "0"} coupons seront generes avec des suffixes aleatoires.
          </p>
        </div>
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function Coupons() {
  const [filters, setFilters] = useState<CouponListParams>({});
  const [search, setSearch] = useState("");
  const [singleOpen, setSingleOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const { data, isLoading, isError } = useCoupons(filters);
  const revoke = useRevokeCoupon();
  const allCoupons = data?.coupons ?? [];

  // Client-side search
  const coupons = useMemo(() => {
    if (!search.trim()) return allCoupons;
    const q = search.toUpperCase();
    return allCoupons.filter(
      (c) =>
        c.code.includes(q) ||
        c.promotion_name.toLowerCase().includes(search.toLowerCase()),
    );
  }, [allCoupons, search]);

  const activeCount = allCoupons.filter((c) => c.status === "active").length;
  const usedCount = allCoupons.filter((c) => c.status === "used").length;
  const revokedCount = allCoupons.filter((c) => c.status === "revoked").length;
  const totalUses = allCoupons.reduce((acc, c) => acc + c.uses_count, 0);

  const handleRevoke = (id: string) => {
    revoke.mutate(id, {
      onSuccess: () => toast.success("Coupon revoque."),
      onError: () => toast.error("Erreur lors de la revocation."),
    });
  };

  const handleCopy = (code: string) => {
    navigator.clipboard
      .writeText(code)
      .then(() => toast.success("Code copie."));
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <Ticket className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Coupons
              </h1>
              <p className="text-sm text-stone-500">
                Codes promo associes aux promotions
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setBulkOpen(true)}>
              Creer en masse
            </Button>
            <Button onClick={() => setSingleOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Creer coupon
            </Button>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <KPICard label="Total coupons" value={allCoupons.length} format="number" />
          <KPICard label="Actifs" value={activeCount} format="number" />
          <KPICard label="Utilisés" value={usedCount} format="number" />
          <KPICard label="Utilisations totales" value={totalUses} format="number" />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher par code ou promotion..."
              className="w-full rounded-lg border border-stone-200 bg-white pl-9 pr-3 py-2 text-sm text-stone-700 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-300"
            />
          </div>
          <select
            value={filters.status ?? ""}
            onChange={(e) =>
              setFilters({
                ...filters,
                status: (e.target.value as CouponStatus) || undefined,
              })
            }
            className="rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm text-stone-700 focus:outline-none focus:ring-2 focus:ring-amber-300"
          >
            <option value="">Tous les statuts</option>
            {(Object.keys(STATUS_LABELS) as CouponStatus[]).map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </div>

        {/* Table */}
        {isLoading && (
          <div className="space-y-3 animate-pulse">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-12 rounded bg-stone-100" />
            ))}
          </div>
        )}

        {isError && (
          <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            Impossible de charger les coupons.
          </div>
        )}

        {!isLoading && !isError && coupons.length === 0 && (
          <EmptyState
            icon={<Ticket className="h-6 w-6" />}
            title="Aucun coupon"
            description={
              search
                ? `Aucun resultat pour "${search}".`
                : "Creez votre premier coupon pour le distribuer."
            }
          />
        )}

        {!isLoading && !isError && coupons.length > 0 && (
          <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-100 bg-stone-50">
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">
                      Code
                    </th>
                    <th className="hidden sm:table-cell px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">
                      Promotion
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">
                      Utilisations
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">
                      Statut
                    </th>
                    <th className="hidden lg:table-cell px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">
                      Cree le
                    </th>
                    <th className="w-24 px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {coupons.map((c) => (
                    <tr
                      key={c.id}
                      className="hover:bg-stone-50 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <span className="font-mono text-sm font-semibold text-stone-800">
                          {c.code}
                        </span>
                      </td>
                      <td className="hidden sm:table-cell px-4 py-3 text-stone-600">
                        {c.promotion_name}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <UsageIndicator
                          usesCount={c.uses_count}
                          maxUses={c.max_uses}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${STATUS_STYLES[c.status]}`}
                        >
                          {STATUS_LABELS[c.status]}
                        </span>
                      </td>
                      <td className="hidden lg:table-cell px-4 py-3 text-stone-500">
                        {new Date(c.created_at).toLocaleDateString("fr-SN")}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            title="Copier le code"
                            onClick={() => handleCopy(c.code)}
                            className="rounded-md p-1.5 text-stone-400 hover:bg-stone-100 hover:text-stone-700"
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </button>
                          {c.status === "active" && (
                            <button
                              title="Révoquer"
                              onClick={() => handleRevoke(c.id)}
                              className="rounded-md p-1.5 text-stone-400 hover:bg-red-50 hover:text-red-700"
                            >
                              <XCircle className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <CreateCouponModal open={singleOpen} onClose={() => setSingleOpen(false)} />
      <BulkCreateModal open={bulkOpen} onClose={() => setBulkOpen(false)} />
    </AdminLayout>
  );
}
